import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@shared/v2/Button";
import FieldLabel from "@shared/v2/FieldLabel";
import Modal from "@shared/v2/Modal";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Loading from "../../Goals/components/Loader";
import LeadMatch from "./LeadMatch";

const LeadMatchesModal = ({ open, onClose, onContinue, person }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    caseTransformingAxios
      .get(`/leads/${person.person_lead_id}/matches`)
      .then((res) => {
        setLoading(false);
        setData(res.data);
      })
      .catch(() => {
        setLoading(false);
        setData(null);
      });
  }, [open, person]);

  return (
    <Modal
      show={open}
      onHide={onClose}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[720px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
    >
      <Modal.Header title="Verify Lead Information" closeButton />

      <form
        className="tw-flex tw-flex-col tw-gap-[32px]"
        onSubmit={(e) => {
          e.preventDefault();
          const prefilledValues = new FormData(e.target).entries().reduce((acc, [key, value]) => {
            if (key === "phoneDetailsAttributes" || key === "emailDetailsAttributes") {
              if (!acc[key]) acc[key] = [];
              acc[key].push({
                category: "other",
                ...(key === "phoneDetailsAttributes" && { name: "phone" }),
                position: acc[key].length + 1,
                value,
                tempId: Date.now(),
              });
            } else {
              acc[key] = value;
            }

            return acc;
          }, {});
          onContinue({ id: person.person_id, prefilledValues });
          onClose();
        }}
      >
        <Modal.Body className="tw-relative tw-flex tw-flex-col tw-gap-[24px]">
          {loading && <Loading />}

          <div className="tw-flex tw-flex-col tw-gap-[8px] tw-p-[8px] tw-border-solid tw-border-[1px] tw-border-theme-primary">
            {data?.searchCriteria.firstAddress && (
              <div>
                <FieldLabel label="For Address" />
                <span>
                  {data.searchCriteria.firstAddress.streetAddress} {data.searchCriteria.firstAddress.city},{" "}
                  {data.searchCriteria.firstAddress.locality} {data.searchCriteria.firstAddress.postalCode}
                </span>
              </div>
            )}

            {data?.searchCriteria.primaryPhoneNumber && (
              <div>
                <FieldLabel label="For Phone Number" />
                <span>{data.searchCriteria.primaryPhoneNumber}</span>
              </div>
            )}

            {data?.searchCriteria.fullName && (
              <div>
                <FieldLabel label="For Name" />
                <span>{data.searchCriteria.fullName}</span>
              </div>
            )}
          </div>

          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            {data?.matches?.map((match) => (
              <LeadMatch key={match.id} match={match} />
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
          <Button size="medium" schema="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="medium" onClick={onContinue}>
            Continue
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

LeadMatchesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  person: PropTypes.shape({
    person_id: PropTypes.number.isRequired,
    person_lead_id: PropTypes.number.isRequired,
  }).isRequired,
};

export default LeadMatchesModal;
